import { FC, useState } from 'react'
import { RenderIf } from '../../../components/RenderIf'
import { errorHandler } from '../../../components/ErrorHandler'
import { getCategoriesData } from 'featureSliced/shared/store/reducers/CategoriesReducer/actionCreator'
import { handleLocalStorage } from '../../../featureSliced/shared/utils/handleLocalStorage'
import { FiltersSlice } from 'featureSliced/shared/store/reducers/FiltersReducer'
import { useMySelector } from 'featureSliced/shared/hooks/useMySelector'
import { useMyDispatch } from '../../../featureSliced/shared/hooks/useMyDispatch'
import { Search } from '../../../components/Search'
import { TimeLocation } from 'featureSliced/widgets/TimeLocationNew'
import { useOpenModal } from '../../../featureSliced/shared/hooks/useOpenModal'
import { AreYouSure } from './AreYouSure_Modal'
import { usePromptParams } from '../../../featureSliced/shared/hooks/usePromptParams'
import Animated from 'react-mount-animation'

import { IconStroked } from '../../../components/IconsContainers/styled'
import { toggleScroll } from '../../../featureSliced/shared/utils/toggleScroll'
import { AppSlice } from 'featureSliced/shared/store/reducers/AppReducer'
import { AllergenSlider } from './AllergenSlider'
import { QRMBucketSlice } from 'featureSliced/shared/store/reducers/QRMenuBucketReducer'

import { ReactComponent as ChevronDown } from 'assets/chevronDown.svg'
import { ReactComponent as CrossImg } from 'assets/cross.svg'
import {
  SliderContainer,
  ExtrasContainer,
  Slider,
  Grid,
  SliderItem,
  GridItem,
  CategoriesOpener,
  EmojiContainer,
  CategoriesCloser,
  CrossImageContainer,
  TimeLocationMenuContainer,
} from './styled'
import { useGetTranslatedNames } from 'featureSliced/shared/hooks/useGetTranslatedNames'
import { getContrastColor } from 'featureSliced/shared/utils/getContrastColor'
import { isCategoryAvailableToday } from 'featureSliced/shared/utils/isCategoryAvailableToday'

type CategoriesSliderProps = {
  isInputShowing: boolean
  hideInput: () => void
  reloadMenuData: () => Promise<void>
  applyCategoriesFilter: () => void
}

export const CategoriesSlider: FC<CategoriesSliderProps> = ({
  isInputShowing,
  hideInput,
  reloadMenuData,
  applyCategoriesFilter,
}) => {
  const dispatch = useMyDispatch()
  const openModal = useOpenModal()
  const openPromptModal = usePromptParams()
  const { getCategoryName } = useGetTranslatedNames()

  const {
    addAllergen,
    deleteAllergen,
    addDiet,
    deleteDiet,
    addSearch,
    discardSearch,
  } = FiltersSlice.actions

  // Store
  const { updateLastActiveCategoryId } = AppSlice.actions

  const isProductsLoaded = useMySelector((state) => state.categories.isLoaded)
  const { colour } = useMySelector((state) => state.app.interfaceStyles)
  const { isTopScrolled } = useMySelector((state) => state.common)
  const categories = useMySelector((state) => state.categories.items)
  const { interfaceStyles, lastActiveCategoryId } = useMySelector(
    (state) => state.app
  )
  const { allergens: selectedAllergens, diet } = useMySelector(
    (state) => state.filters
  )

  // State
  const [inputValue, setInputValue] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<number>(0)
  const [displayType, setDisplayType] = useState<'slider' | 'grid'>('slider')

  // Functions
  const setSelected = async (originId: number | string) => {
    let id: number | string

    if (typeof originId === 'number') {
      id = originId
    } else {
      id = originId.split('_')[originId.split('_').length - 1]
    }

    const listOfElements: any =
      document.getElementsByClassName(`slider_category`)

    const newElementIndex = Array.prototype.findIndex.call(
      listOfElements,
      (element: any) => {
        return element.id === `slider_category_${id}`
      }
    )

    let activeElementIndex = Array.prototype.findIndex.call(
      listOfElements,
      (element: any) => {
        return element.className.includes('SLIDER_BUTTON--ACTIVE')
      }
    )
    if (activeElementIndex === -1) {
      activeElementIndex = Array.prototype.findIndex.call(
        listOfElements,
        (element: any) => {
          return element.id === lastActiveCategoryId
        }
      )
    }

    const sliderElement: any = document.getElementById(`slider_category_${id}`)
    const listElement: any = document.getElementById(`container_category_${id}`)

    Array.prototype.findIndex.call(
      [...listOfElements, sliderElement],
      (element: any) => {
        element.style = null
        element.classList.remove('SLIDER_BUTTON--TARGET')
      }
    )

    if (sliderElement) {
      sliderElement.classList.add('SLIDER_BUTTON--TARGET')
      // sliderElement.style['background'] = interfaceStyles.colour
      sliderElement.style[
        'border-bottom'
      ] = `3px solid ${interfaceStyles.colour}`
      // sliderElement.style['color'] = getContrastColor(interfaceStyles.colour)
      sliderElement.scrollIntoView({ inline: 'center', behavior: 'auto' })
    }

    if (listElement) {
      if (activeElementIndex >= newElementIndex) {
        // listElement.scrollIntoView({ block: 'start', behavior: 'auto' })
        const scrollMarginTopPx = parseFloat(
          window.getComputedStyle(listElement).scrollMarginTop || '0'
        )
        const rect = listElement.getBoundingClientRect()
        window.scrollTo(
          0,
          rect.top + window.scrollY - scrollMarginTopPx - getExtraOffset()
        )
      }
      if (newElementIndex > activeElementIndex) {
        const dims = listElement.getBoundingClientRect()
        window.scrollTo(0, dims.top + window.scrollY - getAdditionalOffset())
      }
    }
    dispatch(updateLastActiveCategoryId(`slider_category_${id}`))
  }

  const slideToView = (id: string) => {
    const sliderElement: any = document.getElementById(id)
    if (sliderElement) {
      sliderElement.classList.add('SLIDER_BUTTON--ACTIVE')
      // sliderElement.style['background'] = 'red'
      sliderElement.style[
        'border-bottom'
      ] = `3px solid ${interfaceStyles.colour}`
      // sliderElement.style['color'] = getContrastColor(interfaceStyles.colour)
      sliderElement.scrollIntoView({ inline: 'center', behavior: 'auto' })
    }
  }

  const getAdditionalOffset = () => {
    const extraOffset = getExtraOffset()
    if (process.env.REACT_APP_IS_CLIC === '0') {
      return 55 + extraOffset
    } else {
      return 175 + extraOffset // depends on banner height
    }
  }

  const getExtraOffset = () => {
    const inputHeight = isInputShowing ? 50 : 0
    const allergensHeight =
      !!selectedAllergens?.length || !!diet?.length ? 28 : 0
    return inputHeight + allergensHeight
  }

  const searchProducts = () => {
    if (inputValue) {
      dispatch(addSearch(inputValue))
    } else {
      dispatch(discardSearch())
    }
  }

  const handleSelectedAllergens = (id: number) => {
    if (selectedAllergens.includes(id)) {
      dispatch(deleteAllergen(id))
    }
    if (!selectedAllergens.includes(id)) {
      dispatch(addAllergen(id))
    }
    applyCategoriesFilter()
  }

  const handleSelectedDietItems = (id: string) => {
    if (diet.includes(id)) {
      dispatch(deleteDiet(id))
    }
    if (!diet.includes(id)) {
      dispatch(addDiet(id))
    }
    applyCategoriesFilter()
  }

  const getBannerCustomTop = (
    isTopScrolled: boolean,
    isInputShowing: boolean,
    isAllergensShowing: boolean
  ) => {
    // let height = 60
    let height = 40
    if (isTopScrolled) height += 70
    if (isAllergensShowing) height += 27
    if (isInputShowing) height += 50
    return `${height}px`
  }

  const getOpenerTop = (
    isTopScrolled: boolean,
    isInputShowing: boolean,
    isAllergensShowing: boolean
  ) => {
    // let height = 60
    let height = 0
    if (isTopScrolled) height += 70
    // if (isAllergensShowing) height += 27
    // if (isInputShowing) height += 50
    return `${height}px`
  }

  const editTimeLocation = () => {
    openModal({
      id: 'ARE_YOU_SURE_PROMPT',
      // TODO: translations
      title: `Are you sure?`,
      components: [
        ({ closeThisModal }) => (
          <AreYouSure
            onDiscardConfirm={() => {
              // The prompt tells the customer their selected products will be discarded
              // (clickCollect.discardDisclaimer) — a new pickup date can make lead-time
              // items in the cart invalid, so the cart must actually be cleared, not just
              // reloaded (QRM-639).
              dispatch(QRMBucketSlice.actions.clearBucket())
              openPromptModal(reloadMenuData, ['time', 'branch'], true)
            }}
            closeModal={closeThisModal}
          />
        ),
      ],
    })
  }

  const handleCategoryClick = (id: number) => {
    setSelectedCategory(id)
    setSelected(id)
  }

  const handleGridCategoryClick = (id: number) => {
    setDisplayType('slider')
    setSelectedCategory(id)
    setSelected(id)
  }

  const handleCategoriesType = () => {
    // hideInput()
    setDisplayType((prevValue) => {
      if (prevValue === 'grid') {
        return 'slider'
      } else {
        setSelectedCategory(0)
        return 'grid'
      }
    })
  }

  const getSlideStyles = (index: number) => {
    if (!lastActiveCategoryId?.length && !index) {
      // return { background: colour, color: getContrastColor(colour) }
      return { borderBottom: `3px solid ${colour}` }
    } else {
      return {}
    }
  }

  const getIsEmojiSelected = (categoryId: number) =>
    `slider_category_${categoryId}` === lastActiveCategoryId

  const getGridStyles = (categoryId: number) => {
    if (`slider_category_${categoryId}` === lastActiveCategoryId) {
      return { background: colour, color: getContrastColor(colour) }
    } else {
      return {}
    }
  }

  return (
    <div>
      <SliderContainer isTopScrolled={isTopScrolled}>
        <Animated.div
          show
          time={0.001}
          delay={0.15}
          unmountDelay={0}
          onUnmountEnd={() => toggleScroll(true)}
          onMountEnd={() => {
            if (selectedCategory) {
              setSelected(selectedCategory)
            } else if (lastActiveCategoryId?.length) {
              slideToView(lastActiveCategoryId)
            }
          }}
          style={{
            position: 'inherit',
            width: '100vw',
            overflow: 'scroll',
          }}
        >
          <Slider>
            {categories
              ?.filter((cat) => isCategoryAvailableToday(cat))
              ?.map((category, i) => {
                return (
                  <SliderItem
                    key={`slider_${category.id}`}
                    className="slider_category"
                    id={`slider_category_${category.id}`}
                    style={getSlideStyles(i)}
                    onClick={() => handleCategoryClick(category.id)}
                  >
                    {getCategoryName(category)}
                    {/* {category.name} */}
                  </SliderItem>
                )
              })}
          </Slider>
        </Animated.div>
        <Animated.div
          show={displayType === 'grid'}
          mountAnim={`
            0% {max-height: 60px}
            100% {max-height: 70vh}
          `}
          unmountAnim={`
            0% {max-height: 70vh}
            100% {max-height: 0px}
          `}
          time={0.6}
          unmountTime={0.2}
          onUnmountEnd={() => toggleScroll(false)}
        >
          <Grid>
            {categories
              ?.filter((cat) => isCategoryAvailableToday(cat))
              ?.map((category, i) => {
                return (
                  <GridItem
                    key={`grid_${category.id}`}
                    className="slider_category"
                    // id={`slider_category_${category.id}`}
                    style={getGridStyles(category.id)}
                    onClick={() => handleGridCategoryClick(category.id)}
                  >
                    {getCategoryName(category)}
                    {/* {category.name} */}
                  </GridItem>
                )
              })}
          </Grid>
          <CategoriesCloser onClick={handleCategoriesType}>
            <CrossImageContainer>
              <IconStroked color="#fff">
                <CrossImg />
              </IconStroked>
            </CrossImageContainer>
          </CategoriesCloser>
        </Animated.div>
      </SliderContainer>
      <RenderIf condition={isInputShowing}>
        <ExtrasContainer isTopScrolled={isTopScrolled} customPadding="40px">
          <Search
            inputValue={inputValue}
            setInputValue={setInputValue}
            onSearchClick={searchProducts}
          />
        </ExtrasContainer>
      </RenderIf>
      <RenderIf condition={!!selectedAllergens?.length || !!diet?.length}>
        <ExtrasContainer
          isTopScrolled={isTopScrolled}
          customPadding={isInputShowing ? '90px' : '40px'}
        >
          <AllergenSlider
            selectedAllergens={selectedAllergens}
            diet={diet}
            handleSelectedAllergens={handleSelectedAllergens}
            handleSelectedDietItems={handleSelectedDietItems}
          />
        </ExtrasContainer>
      </RenderIf>
      <RenderIf condition={process.env.REACT_APP_IS_CLIC === '1'}>
        <TimeLocationMenuContainer
          customTop={getBannerCustomTop(
            isTopScrolled,
            isInputShowing,
            !!selectedAllergens?.length || !!diet?.length
          )}
        >
          <TimeLocation editTimeLocation={editTimeLocation} />
        </TimeLocationMenuContainer>
      </RenderIf>
      <RenderIf condition={displayType === 'slider' && isProductsLoaded}>
        <CategoriesOpener
          onClick={handleCategoriesType}
          customTop={getOpenerTop(
            isTopScrolled,
            isInputShowing,
            !!selectedAllergens?.length || !!diet?.length
          )}
        >
          <IconStroked color="#fff">
            <ChevronDown />
          </IconStroked>
        </CategoriesOpener>
      </RenderIf>
    </div>
  )
}
